import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cx } from '../../ui';
import { heroUnits, type HeroUnit } from '../../heroUnits';
import { Check, Stage, reduced, useVisible } from './kit';

// Hero centrepiece: a partner (installer / service partner) scans the QR on a unit's rating plate, runs the
// handover checklist on a phone, the owner signs, and the manufacturer's registrations table updates
// (warranty started, partner credited). Each loop features the next fictional unit from heroUnits
// (heat pump, ride-on mower, oven, e-bike). Product imagery is photography only (no drawn
// products); until the catalogue shots exist, images fall back to a crop of an editorial photo.
// Looping timeline, paused off-screen, static final frame (first unit) for reduced motion.

const FALLBACK_IMG = '/images/v2/gen/heatpump-commission-b.jpg';
const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

// Per-unit supporting data for the admin table (all fictional).
const LOCAL: Partial<Record<HeroUnit['key'], { place: string; years: number; partners: [string, string]; others: [string, string][] }>> = {
  heatpump: { place: 'Ludlow', years: 7, partners: ['Severn Renewables', 'Clee Hill Heat'], others: [['3 Mill Close', 'Leominster'], ['The Old Forge', 'Church Stretton'], ['27 Castle View', 'Bridgnorth'], ['Ashford Farmhouse', 'Tenbury']] },
  mower: { place: 'Hexham', years: 5, partners: ['Fenwick Garden Machinery', 'Tynedale Mowers'], others: [['The Old Rectory', 'Corbridge'], ['Riverside Park', 'Hexham'], ['Dene Farm', 'Allendale'], ['Haydon Golf Club', 'Haydon Bridge']] },
  oven: { place: 'Whitby', years: 2, partners: ['Coastline Kitchen Engineers', 'Dales Catering Repairs'], others: [['Moorland Bread Co.', 'Pickering'], ['St Hilda’s School', 'Whitby'], ['The Pantry, Malton', 'Malton'], ['Riverside Bakehouse', 'York']] },
  ebike: { place: 'Edinburgh', years: 2, partners: ['Canal Street Cycles', 'Bruntsfield Bikes'], others: [['Sam, Portobello', 'Edinburgh'], ['Ana, Marchmont', 'Edinburgh'], ['Tom, Stockbridge', 'Edinburgh'], ['Priya, Musselburgh', 'East Lothian']] },
};

type View = HeroUnit & { short: string; tail: string; prefix: string; role: string; Role: string; done: string; sign: string; place: string; years: number };
function view(u: HeroUnit): View {
  const parts = u.serial.split('-');
  const commissioning = u.handoverName === 'Commissioning';
  return {
    ...u,
    short: u.model.replace(u.brand + ' ', ''),
    tail: parts[parts.length - 1],
    prefix: parts.slice(0, -1).join('-'),
    role: u.partnerRole,
    Role: cap(u.partnerRole),
    done: commissioning ? 'Commissioned' : 'Signed off',
    sign: commissioning ? 'Sign and commission' : 'Sign off installation',
    place: LOCAL[u.key]!.place,
    years: LOCAL[u.key]!.years,
  };
}

// Photo with a guarded fallback: the editorial crop until the catalogue shot exists, then a neutral block.
function UnitImg({ src, alt = '', className, fit = 'cover', crop }: { src: string; alt?: string; className?: string; fit?: 'cover' | 'contain'; crop: { pos: string; scale: number } }) {
  const [stage, setStage] = useState<0 | 1 | 2>(0);
  useEffect(() => setStage(0), [src]);
  if (stage === 2) return <div className={cx('bg-[#E4E2EC]', className)} aria-hidden="true" />;
  const fallback = stage === 1;
  return (
    <div className={cx('overflow-hidden', className)}>
      <img
        src={fallback ? FALLBACK_IMG : src}
        alt={alt}
        draggable={false}
        onError={() => setStage((x) => (x === 0 ? 1 : 2))}
        className="block h-full w-full"
        style={fallback ? { objectFit: 'cover', objectPosition: crop.pos, transform: `scale(${crop.scale})`, transformOrigin: crop.pos } : { objectFit: fit }}
      />
    </div>
  );
}

// step: 0 camera, 1 locked, 2 checks, 3 signing, 4 done
const SCHEDULE = [1800, 900, 2500, 1400, 4600];

export function useLoop(active: boolean) {
  const [step, setStep] = useState(() => (reduced() ? 4 : 0));
  const [cycle, setCycle] = useState(0);
  useEffect(() => {
    if (!active || reduced()) return;
    const id = window.setTimeout(() => {
      if (step === SCHEDULE.length - 1) {
        setStep(0);
        setCycle((c) => c + 1);
      } else setStep(step + 1);
    }, SCHEDULE[step]);
    return () => window.clearTimeout(id);
  }, [step, active]);
  return { step, cycle };
}

// A believable QR: finder patterns + seeded modules.
export function QR({ size = 48, fg = '#111', bg = '#fff', seed = 7 }: { size?: number; fg?: string; bg?: string; seed?: number }) {
  const n = 25;
  const cells: React.ReactNode[] = [];
  let s = seed * 9301 + 49297;
  const rnd = () => ((s = (s * 9301 + 49297) % 233280) / 233280);
  const inFinder = (x: number, y: number) => (x < 8 && y < 8) || (x > n - 9 && y < 8) || (x < 8 && y > n - 9);
  for (let y = 0; y < n; y++)
    for (let x = 0; x < n; x++) {
      if (inFinder(x, y)) continue;
      if (rnd() > 0.52) cells.push(<rect key={`${x}-${y}`} x={x} y={y} width="1.02" height="1.02" />);
    }
  const finder = (x: number, y: number) => (
    <g key={`f${x}${y}`}>
      <rect x={x} y={y} width="7" height="7" />
      <rect x={x + 1} y={y + 1} width="5" height="5" fill={bg} />
      <rect x={x + 2} y={y + 2} width="3" height="3" />
    </g>
  );
  return (
    <svg width={size} height={size} viewBox={`-1 -1 ${n + 2} ${n + 2}`} aria-hidden="true" shapeRendering="crispEdges">
      <rect x="-1" y="-1" width={n + 2} height={n + 2} fill={bg} />
      <g fill={fg}>
        {cells}
        {finder(0, 0)}
        {finder(n - 7, 0)}
        {finder(0, n - 7)}
      </g>
    </svg>
  );
}

function PanelLabel({ u, locked }: { u: View; locked: boolean }) {
  return (
    <div className="relative rounded-[5px] bg-white p-[9px] text-left" style={{ width: 176, boxShadow: 'inset 0 0 0 1px #D5D9DC, 0 6px 14px rgba(0,0,0,.28)' }}>
      <div className="flex gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] font-extrabold tracking-[0.12em]" style={{ color: u.accent }}>
            {u.brand.toUpperCase()}
          </div>
          <div className="mt-1 space-y-[2px] font-mono text-[6.6px] leading-[1.35] text-[#4A4D52]">
            <div>MODEL&nbsp; {u.short}</div>
            <div>SERIAL {u.prefix}</div>
            <div>&nbsp; &nbsp; &nbsp; -{u.tail}</div>
            <div>YEAR&nbsp;&nbsp; 2026</div>
            <div>MADE IN EU</div>
          </div>
        </div>
        <div className="relative mt-1 h-[58px] w-[58px] shrink-0 rounded-[3px] bg-white p-[3px]" style={{ boxShadow: 'inset 0 0 0 1px #E3E6E8' }}>
          <QR size={52} seed={17} fg="#1B1C1E" />
          {/* viewfinder lock */}
          <span
            className="vb-cut pointer-events-none absolute -inset-[7px] rounded-[6px]"
            style={{ boxShadow: `0 0 0 2px ${locked ? '#CFF710' : 'transparent'}`, opacity: locked ? 1 : 0 }}
          />
        </div>
      </div>
    </div>
  );
}

function PhoneShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative" style={{ width: 236, height: 474, borderRadius: 40, padding: 8, background: '#16123D', boxShadow: '0 50px 80px -34px rgba(24,19,67,.6), 0 0 0 1px rgba(24,19,67,.4), inset 0 0 0 1.5px rgba(255,255,255,.1)' }}>
      <div className="relative h-full w-full overflow-hidden bg-white" style={{ borderRadius: 32 }}>
        <div className="absolute top-[7px] z-30 h-[19px] w-[74px] rounded-full bg-[#16123D]" style={{ left: 73 }} />
        {children}
      </div>
    </div>
  );
}

function StatusBar({ dark }: { dark?: boolean }) {
  return (
    <div className={cx('relative z-20 flex h-[32px] items-center justify-between px-5 pt-1 text-[10px] font-semibold', dark ? 'text-white' : 'text-[#1B1B1F]')}>
      <span>09:41</span>
      <span className="flex items-center gap-1">
        <svg width="14" height="9" viewBox="0 0 14 9" aria-hidden="true">
          <rect x="0" y="6" width="2.4" height="3" rx=".5" fill="currentColor" />
          <rect x="3.6" y="4" width="2.4" height="5" rx=".5" fill="currentColor" />
          <rect x="7.2" y="2" width="2.4" height="7" rx=".5" fill="currentColor" />
          <rect x="10.8" y="0" width="2.4" height="9" rx=".5" fill="currentColor" />
        </svg>
        <span className="inline-block h-[8px] w-[16px] rounded-[2.5px] border border-current p-[1px]">
          <span className="block h-full w-[70%] rounded-[1px] bg-current" />
        </span>
      </span>
    </div>
  );
}

function PhoneScreens({ u, step }: { u: View; step: number }) {
  const camera = step <= 1;
  const done = step >= 4;
  return (
    <PhoneShell>
      {/* 1. Camera */}
      <div className="vb-cut absolute inset-0" style={{ opacity: camera ? 1 : 0, background: '#3a3530' }}>
        <UnitImg src={u.product} className="absolute inset-0" crop={{ pos: '99% 40%', scale: 1.5 }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,.45) 0%, rgba(0,0,0,.08) 30%, rgba(0,0,0,.1) 70%, rgba(0,0,0,.5) 100%)' }} />
        <StatusBar dark />
        <div className="absolute left-[22px] top-[146px]" style={{ transform: `scale(${step === 1 ? 1.06 : 1})`, transformOrigin: 'center', transition: 'transform .5s var(--bm-ease)' }}>
          <PanelLabel u={u} locked={step === 1} />
        </div>
        {/* viewfinder corners */}
        <div className="absolute left-[22px] right-[22px] top-[112px] h-[190px]">
          {['left-0 top-0 border-l-2 border-t-2 rounded-tl-[14px]', 'right-0 top-0 border-r-2 border-t-2 rounded-tr-[14px]', 'left-0 bottom-0 border-l-2 border-b-2 rounded-bl-[14px]', 'right-0 bottom-0 border-r-2 border-b-2 rounded-br-[14px]'].map((c) => (
            <span key={c} className={cx('absolute h-6 w-6 border-white/90', c)} />
          ))}
          {step === 0 && <span className="vb-scanline" />}
        </div>
        <div className="absolute inset-x-0 bottom-[54px] flex justify-center">
          <span className="vb-cut rounded-full bg-black/55 px-3 py-1.5 text-[10.5px] font-medium text-white" style={{ backdropFilter: 'blur(6px)' }}>
            {step === 1 ? (
              <span className="flex items-center gap-1.5">
                <span className="grid h-3.5 w-3.5 place-items-center rounded-full bg-[#CFF710] text-[#241E61]">
                  <Check size={9} />
                </span>
                {u.short} · {u.serial}
              </span>
            ) : (
              'Point at the QR on the rating plate'
            )}
          </span>
        </div>
        <div className="absolute bottom-[14px] left-[65px] h-[4px] w-[90px] rounded-full bg-white/70" />
      </div>

      {/* 2. Partner handover checklist */}
      <div className="vb-cut absolute inset-0 bg-white" style={{ opacity: !camera && !done ? 1 : 0, transform: !camera && !done ? 'none' : camera ? 'translateY(14px)' : 'none' }}>
        <StatusBar />
        <div className="flex items-center justify-between px-3.5 pb-2">
          <div className="flex items-center gap-1.5">
            <span className="grid h-[18px] w-[18px] place-items-center rounded-[5px] text-[9px] font-extrabold text-white" style={{ background: u.accent }}>
              {u.brand[0]}
            </span>
            <span className="text-[10.5px] font-extrabold tracking-[0.05em]" style={{ color: '#1F2A30' }}>
              {u.brand.toUpperCase()}
            </span>
          </div>
          <span className="whitespace-nowrap rounded-full bg-[#F1F0F7] px-2 py-[3px] text-[8.5px] font-medium text-[#4C4970]">{u.Role} view</span>
        </div>
        <UnitImg src={u.product} className="mx-3.5 h-[78px] rounded-[12px] bg-[#F1F0F7]" crop={{ pos: '97% 52%', scale: 3 }} />
        <div className="px-3.5 pt-2.5">
          <div className="text-[14px] font-bold leading-tight text-[#1C1B22]">{u.model}</div>
          <div className="mt-1 flex items-center gap-1.5">
            <span className="rounded-[4px] bg-[#F1F0F7] px-1.5 py-[2px] font-mono text-[8.5px] text-[#3A3760]">{u.serial}</span>
            <span className="text-[8.5px] font-medium text-[#6F6C8E]">{u.kind}</span>
          </div>
          <div className="mt-3 text-[9px] font-semibold uppercase tracking-[0.08em] text-[#6F6C8E]">{u.handoverName}</div>
          <ul className="mt-1.5 space-y-[5px]">
            {u.checks.map((c, i) => {
              const on = step >= 2;
              return (
                <li key={c} className="flex items-center gap-2 text-[10px] text-[#2A2840]">
                  <span
                    className="vb-tick grid h-[14px] w-[14px] shrink-0 place-items-center rounded-[4px] text-white"
                    style={{ background: on ? '#2F9E58' : '#fff', boxShadow: on ? 'none' : 'inset 0 0 0 1.4px #CFCDE0', transitionDelay: on ? `${200 + i * 480}ms` : '0ms' }}
                  >
                    <Check size={9} />
                  </span>
                  {c}
                </li>
              );
            })}
          </ul>
          <div className="mt-3 grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1 text-[9.5px]">
            <span className="text-[#6F6C8E]">Owner</span>
            <span className="truncate rounded-[6px] px-2 py-1 font-medium text-[#1C1B22]" style={{ boxShadow: 'inset 0 0 0 1px #E4E3F0' }}>
              {u.owner}
            </span>
          </div>
          <div className="relative mt-2 h-[44px] rounded-[8px] bg-[#FAFAFD]" style={{ boxShadow: 'inset 0 0 0 1px #E4E3F0' }}>
            <span className="absolute left-2 top-1 text-[8px] text-[#9A98B5]">Owner signature</span>
            <svg className={cx('vb-sig absolute inset-0', step >= 3 && 'is-on')} viewBox="0 0 200 44" preserveAspectRatio="none" aria-hidden="true">
              <path d="M22 32c8-14 14-18 16-10s-4 14 2 10 10-18 16-14-2 12 4 10 12-12 18-10 2 8 8 8 14-8 22-8 12 6 24 2" fill="none" stroke="#241E61" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div
            className="mt-2.5 flex h-[34px] items-center justify-center rounded-full text-[11px] font-semibold text-white transition-transform duration-150"
            style={{ background: step >= 3 ? '#181343' : '#241E61', transform: step === 3 ? 'scale(.97)' : 'none' }}
          >
            {u.sign}
          </div>
        </div>
      </div>

      {/* 3. Done */}
      <div className="vb-cut absolute inset-0 bg-white" style={{ opacity: done ? 1 : 0, transform: done ? 'none' : 'translateY(14px)' }}>
        <StatusBar />
        <div className="flex items-center gap-1.5 px-3.5 pb-2">
          <span className="grid h-[18px] w-[18px] place-items-center rounded-[5px] text-[9px] font-extrabold text-white" style={{ background: u.accent }}>
            {u.brand[0]}
          </span>
          <span className="text-[10.5px] font-extrabold tracking-[0.05em]" style={{ color: '#1F2A30' }}>
            {u.brand.toUpperCase()}
          </span>
        </div>
        <div className="px-3.5 pt-5 text-center">
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-full text-white" style={{ background: '#2F9E58' }}>
            <Check size={24} />
          </span>
          <div className="mt-3 text-[16px] font-bold text-[#1C1B22]">{u.done}</div>
          <div className="mt-0.5 text-[10.5px] text-[#6F6C8E]">Warranty active from today</div>
        </div>
        <div className="mx-3.5 mt-4 rounded-[12px] p-3 text-[10px]" style={{ boxShadow: 'inset 0 0 0 1px #E4E3F0' }}>
          {[
            ['Unit', `${u.short} · ${u.tail}`],
            ['Owner', u.owner],
            [u.Role, u.partner],
            ['Warranty', `19 Oct 2026 to ${2026 + u.years}`],
          ].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between border-b border-[#EFEEF6] py-[6px] last:border-0">
              <span className="text-[#6F6C8E]">{k}</span>
              <span className="font-semibold text-[#1C1B22]">{v}</span>
            </div>
          ))}
        </div>
        <div className="mx-3.5 mt-2.5 flex items-center gap-2 rounded-[10px] bg-[#F7F7FC] px-3 py-2 text-[9.5px] text-[#3A3760]">
          <svg width="16" height="18" viewBox="0 0 16 18" aria-hidden="true">
            <path d="M2 1.5h8l4 4v11H2z" fill="#fff" stroke="#9A98B5" />
            <path d="M5 9h6M5 12h4" stroke="#9A98B5" />
          </svg>
          <span className="flex-1">{u.handoverName} record signed and sealed</span>
          <Check size={11} className="text-[#2F9E58]" />
        </div>
        <div className="absolute inset-x-0 bottom-[16px] text-center text-[8.5px] text-[#9A98B5]">Powered by BrandedMark</div>
      </div>
    </PhoneShell>
  );
}

type Row = { serial: string; owner: string; place: string; partner: string; status: 'active' | 'stock' | 'new'; when: string };
function rowsFor(u: View): Row[] {
  const L = LOCAL[u.key]!;
  const n = parseInt(u.tail, 10);
  const sn = (d: number) => `${u.prefix}-${String(n - d).padStart(u.tail.length, '0')}`;
  return [
    { serial: sn(5), owner: L.others[0][0], place: L.others[0][1], partner: u.partner, status: 'active', when: 'Fri' },
    { serial: sn(9), owner: L.others[1][0], place: L.others[1][1], partner: L.partners[0], status: 'active', when: 'Thu' },
    { serial: sn(19), owner: L.others[2][0], place: L.others[2][1], partner: L.partners[1], status: 'active', when: 'Wed' },
    { serial: sn(3), owner: `With ${u.role}`, place: u.place, partner: u.partner, status: 'stock', when: '' },
    { serial: sn(27), owner: L.others[3][0], place: L.others[3][1], partner: L.partners[0], status: 'active', when: 'Mon' },
  ];
}

function StatusPill({ s }: { s: Row['status'] }) {
  if (s === 'stock')
    return <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-[#F1F0F7] px-2 py-[3px] text-[10px] font-medium text-[#5C5980]">Awaiting install</span>;
  return (
    <span className={cx('inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-[3px] text-[10px] font-medium', s === 'new' ? 'bg-[#E3F5E9] text-[#1F7A43]' : 'bg-[#EEF7F1] text-[#2F7D4F]')}>
      <span className="h-[5px] w-[5px] rounded-full bg-[#3FAE6C]" />
      {s === 'new' ? 'Warranty started' : 'Warranty active'}
    </span>
  );
}

function AdminWindow({ u, step, cycle, compact }: { u: View; step: number; cycle: number; compact?: boolean }) {
  const added = step >= 4;
  const base = rowsFor(u);
  const rows: Row[] = added ? [{ serial: u.serial, owner: u.owner, place: u.place, partner: u.partner, status: 'new', when: 'Just now' }, ...base] : base;
  return (
    <div className="overflow-hidden rounded-[14px] bg-white" style={{ boxShadow: '0 40px 80px -40px rgba(24,19,67,.45), 0 0 0 1px #E1E0EE' }}>
      <div className="flex h-[34px] items-center gap-1.5 border-b border-[#ECEBF4] bg-[#F7F7FC] px-3.5">
        <span className="h-[9px] w-[9px] rounded-full bg-[#E1E0EE]" />
        <span className="h-[9px] w-[9px] rounded-full bg-[#E1E0EE]" />
        <span className="h-[9px] w-[9px] rounded-full bg-[#E1E0EE]" />
        <span className="mx-auto rounded-[6px] bg-white px-10 py-[3px] font-mono text-[10px] text-[#7B789D]" style={{ boxShadow: 'inset 0 0 0 1px #ECEBF4' }}>
          {u.brand.toLowerCase()}.brandedmark.com
        </span>
      </div>
      <div className="flex">
        {!compact && (
          <div className="flex w-[50px] shrink-0 flex-col items-center gap-3 border-r border-[#F0EFF6] pt-3.5">
            <span className="grid h-7 w-7 place-items-center rounded-[8px] text-[12px] font-extrabold text-white" style={{ background: u.accent }}>
              {u.brand[0]}
            </span>
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className={cx('h-7 w-7 rounded-[8px]', i === 0 ? 'bg-[#EEEDF7]' : '')}>
                <span className={cx('m-[9px] block h-[10px] w-[10px] rounded-[3px] border-[1.6px]', i === 0 ? 'border-[#241E61]' : 'border-[#BDBBD4]')} />
              </span>
            ))}
          </div>
        )}
        <div className="min-w-0 flex-1 px-5 pb-4 pt-3.5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[15px] font-bold tracking-[-0.01em] text-[#1C1B22]">Registrations</div>
              <div className="text-[10.5px] text-[#7B789D]">All products · last 7 days</div>
            </div>
            {!compact && (
              <div className="flex items-center gap-1.5">
                <span className="rounded-full px-2.5 py-1 text-[10px] font-medium text-[#3A3760]" style={{ boxShadow: 'inset 0 0 0 1px #E4E3F0' }}>
                  All {u.role}s
                </span>
                <span className="rounded-full px-2.5 py-1 text-[10px] font-medium text-[#3A3760]" style={{ boxShadow: 'inset 0 0 0 1px #E4E3F0' }}>
                  Export
                </span>
              </div>
            )}
          </div>
          {!compact && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {[
                [`${u.done} this week`, added ? '13' : '12'],
                ['Waiting on paperwork', '0'],
                [`${u.Role}s active`, '9'],
              ].map(([k, v], i) => (
                <div key={k} className="rounded-[10px] px-3 py-2" style={{ boxShadow: 'inset 0 0 0 1px #ECEBF4' }}>
                  <div className="text-[9.5px] text-[#7B789D]">{k}</div>
                  <div key={i === 0 ? `${v}-${cycle}` : k} className={cx('text-[17px] font-bold tracking-[-0.02em] text-[#1C1B22]', i === 0 && added && 'vb-toast')}>
                    {v}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className={cx('mt-3 overflow-hidden', compact ? 'h-[178px]' : 'h-[248px]')}>
            {!compact && (
              <div className="grid grid-cols-[1.2fr_1.35fr_0.95fr_1.05fr] gap-2 border-b border-[#ECEBF4] pb-1.5 font-mono text-[9px] uppercase tracking-[0.08em] text-[#9A98B5]">
                <span>Serial</span>
                <span>Owner</span>
                <span>{u.Role}</span>
                <span>Status</span>
              </div>
            )}
            {rows.map((r) => {
              const isNew = r.status === 'new';
              return (
                <div
                  key={isNew ? `new-${cycle}` : r.serial}
                  className={cx(
                    compact ? 'flex items-center justify-between gap-2 border-b border-[#F0EFF6] py-2' : 'grid grid-cols-[1.2fr_1.35fr_0.95fr_1.05fr] items-center gap-2 border-b border-[#F0EFF6] py-[9px]',
                    isNew && 'vb-row-new vb-row-flash -mx-2 rounded-[8px] px-2'
                  )}
                >
                  <span className="truncate font-mono text-[10px] text-[#3A3760]">{compact ? r.serial.slice(r.serial.indexOf('-') + 1) : r.serial}</span>
                  {!compact && (
                    <span className="min-w-0">
                      <span className="block truncate text-[10.5px] font-semibold text-[#1C1B22]">{r.owner}</span>
                      <span className="block truncate text-[9.5px] text-[#8A88A8]">{r.place}</span>
                    </span>
                  )}
                  {!compact && (
                    <span className="flex min-w-0 items-center gap-1 text-[10.5px] text-[#3A3760]">
                      <span className="truncate">{r.partner}</span>
                    </span>
                  )}
                  <StatusPill s={r.status} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Toasts({ u, cycle }: { u: View; cycle: number }) {
  return (
    <div key={cycle} className="space-y-2">
      {[
        { t: `${u.Role} credited`, d: `${u.partner}, ${u.place}`, delay: '0.5s' },
        { t: `Warranty started at ${u.handoverName.toLowerCase()}`, d: `${u.years} years · ends 19 Oct ${2026 + u.years}`, delay: '1.1s' },
        { t: 'Written back to your ERP', d: 'Registration and warranty start date', delay: '1.7s' },
      ].map((x) => (
        <div key={x.t} className="vb-toast flex items-center gap-2.5 rounded-[12px] bg-white px-3 py-2.5" style={{ animationDelay: x.delay, boxShadow: '0 16px 30px -18px rgba(24,19,67,.45), 0 0 0 1px #E4E3F0' }}>
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#E3F5E9] text-[#2F9E58]">
            <Check size={12} />
          </span>
          <span className="min-w-0">
            <span className="block text-[11px] font-semibold leading-tight text-[#1C1B22]">{x.t}</span>
            <span className="block truncate text-[10px] text-[#7B789D]">{x.d}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

// Layouts: 'wide' (stacked hero, full width), 'split' (right-hand column of the split hero from `splitAt`px
// viewport width) and 'compact' (narrow containers / phones).
type Mode = 'wide' | 'split' | 'compact';

export function HeroVisual({ className, splitAt, onUnit }: { className?: string; splitAt?: number; onUnit?: (index: number) => void }) {
  const [ref, visible] = useVisible<HTMLDivElement>(0.15);
  const { step, cycle } = useLoop(visible);
  const index = cycle % heroUnits.length;
  const u = view(heroUnits[index]);
  useEffect(() => onUnit?.(index), [index, onUnit]);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<Mode>('wide');
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const fit = () => setMode(splitAt && window.innerWidth >= splitAt && el.clientWidth >= 420 ? 'split' : el.clientWidth < 600 ? 'compact' : 'wide');
    fit();
    window.addEventListener('resize', fit);
    if (!('ResizeObserver' in window)) return () => window.removeEventListener('resize', fit);
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [splitAt]);

  const caption = [`${u.Role} scans the rating plate`, 'Unit found', `${u.handoverName} checks on the phone`, 'Owner signs on the phone', `${u.done}. Warranty active. You see it.`][step];
  const label = `Animation: a ${u.role} scans the QR on the rating plate of a ${u.model} ${u.kind.toLowerCase()}, ticks the ${u.handoverName.toLowerCase()} checks and the owner signs on a phone. The manufacturer’s registrations table then shows the unit with warranty started and the ${u.role} credited. Each loop shows a different product.`;

  return (
    <div ref={ref} className={cx('vb-hv', className)}>
      <div ref={wrapRef}>
        {mode === 'split' ? (
          <Stage w={720} h={548} label={label}>
            <div className="absolute right-0 top-0 w-[560px]">
              <AdminWindow u={u} step={step} cycle={cycle} />
            </div>
            <div className="absolute left-0 top-[68px] z-10">
              <PhoneScreens u={u} step={step} />
            </div>
            <div className="absolute right-[-6px] top-[318px] z-10 w-[248px]">{step >= 4 && <Toasts u={u} cycle={cycle} />}</div>
          </Stage>
        ) : mode === 'compact' ? (
          <Stage w={400} h={640} label={label}>
            <div className="absolute left-0 top-0">
              <PhoneScreens u={u} step={step} />
            </div>
            <div className="absolute left-[150px] top-[340px] w-[250px]">
              <AdminWindow u={u} step={step} cycle={cycle} compact />
            </div>
          </Stage>
        ) : (
          <Stage w={1000} h={548} max={1.12} label={label}>
            <div className="absolute left-[250px] top-[8px] w-[600px]">
              <AdminWindow u={u} step={step} cycle={cycle} />
            </div>
            <div className="absolute left-[56px] top-[58px] z-10">
              <PhoneScreens u={u} step={step} />
            </div>
            <div className="absolute right-0 top-[300px] z-10 w-[256px]">{step >= 4 && <Toasts u={u} cycle={cycle} />}</div>
          </Stage>
        )}
      </div>
      <div className="mt-4 flex items-center gap-3" aria-hidden="true">
        <div className="flex gap-1">
          {SCHEDULE.map((_, i) => (
            <span key={i} className="h-[3px] w-6 rounded-full transition-colors duration-200" style={{ background: i <= step ? '#241E61' : '#DCDBEA' }} />
          ))}
        </div>
        <span className="vb-mono text-[12px] text-bm-muted">{caption}</span>
      </div>
    </div>
  );
}
